import { type ReactElement } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useOrgWriteGate } from '@/hooks/useRoleCheck';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { SecurityGroupDetail } from './SecurityGroupDetail';
import { SecurityGroupsList } from './SecurityGroupsList';

/**
 * Security Groups tab (KIR-3800).
 *
 * Flag-gated end to end: with `security_groups_policies` off the backend
 * disables the group APIs, so a direct deep-link redirects to the org root
 * rather than rendering a page whose every request would fail.
 */
export default function SecurityGroupsTab(): ReactElement | null {
  const { organizationId } = useParams<{ organizationId: string }>();

  const { flags, isLoading: isFlagLoading } = useFeatureFlags([
    flagsmithFeatureFlags.SECURITY_GROUPS_POLICIES,
  ]);
  const isEnabled =
    flags[flagsmithFeatureFlags.SECURITY_GROUPS_POLICIES] ?? false;

  // AC #17: owners/managers edit; support gets the page with every write
  // control disabled (KIR-5086), via the same gate as Alerts and Skills.
  const writeGate = useOrgWriteGate(organizationId);
  const canEdit = !writeGate.disabled;

  if (!organizationId) return null;
  // Wait for the role too, so an owner never sees the read-only state flash.
  if (isFlagLoading || writeGate.isLoading) return null;
  if (!isEnabled || writeGate.hidden) {
    return <Navigate to={`/${organizationId}`} replace />;
  }

  return (
    <Routes>
      <Route
        index
        element={
          <SecurityGroupsList
            organizationId={organizationId}
            canEdit={canEdit}
          />
        }
      />
      <Route
        path=":groupId"
        element={
          <SecurityGroupDetail
            organizationId={organizationId}
            canEdit={canEdit}
          />
        }
      />
    </Routes>
  );
}
